/**
 * Контролы фильтров тулбара «Клиенты» (десктоп):
 *   FilterSelect — одиночный выбор с цветной точкой и счётчиком (Статус/Тип).
 *   FilterMulti  — мультивыбор с поиском и чекбоксами (Тариф/Город).
 * Оба — триггер-кнопка (label + значение + шеврон) + Popover.
 */

import {type ReactNode, useState} from 'react';
import {Check, ChevronDown} from 'lucide-react';

import {SearchField} from '@components/SearchField';
import {Popover, PopoverContent, PopoverTrigger} from '@components/ui/Popover';
import {cn} from '@lib/utils';

type TriggerProps = {label: string; value: string};

const Trigger = ({label, value}: TriggerProps) => (
  <PopoverTrigger className="flex h-10 items-center gap-2 rounded-[10px] border border-input bg-card px-[13px] text-[13.5px] text-skr-ink outline-none transition-colors hover:bg-secondary/60 focus-visible:ring-2 focus-visible:ring-ring">
    <span className="text-muted-foreground">{label}</span>
    {value}
    <ChevronDown className="size-3.5 text-muted-foreground" />
  </PopoverTrigger>
);

export type SelectOption = {value: string; label: string; dot?: string; count?: number};

type FilterSelectProps = {
  label: string;
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
};

export const FilterSelect = ({label, options, value, onChange}: FilterSelectProps) => {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value)?.label ?? 'Все';

  const pick = (next: string | null) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Trigger label={label} value={current} />
      <PopoverContent className="w-56">
        <Row active={value === null} onClick={() => pick(null)}>
          Все
        </Row>
        {options.map((o) => (
          <Row key={o.value} active={value === o.value} onClick={() => pick(o.value)}>
            {o.dot && <span className="size-1.5 rounded-full" style={{background: o.dot}} />}
            <span className="flex-1">{o.label}</span>
            {o.count != null && (
              <span className="tnum text-xs text-muted-foreground">{o.count}</span>
            )}
          </Row>
        ))}
      </PopoverContent>
    </Popover>
  );
};

type FilterMultiProps = {
  label: string;
  options: readonly string[];
  selected: string[];
  onChange: (next: string[]) => void;
};

export const FilterMulti = ({label, options, selected, onChange}: FilterMultiProps) => {
  const [search, setSearch] = useState('');
  const value =
    selected.length === 0
      ? 'Все'
      : selected.length === 1
        ? selected[0]
        : `${selected.length} выбрано`;
  const shown = options.filter((o) => o.toLowerCase().includes(search.trim().toLowerCase()));

  const toggle = (opt: string) => {
    onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt]);
  };

  return (
    <Popover>
      <Trigger label={label} value={value} />
      <PopoverContent className="w-64">
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder="Поиск…"
          className="mb-1 h-8 rounded-md border border-input bg-background px-2.5"
        />
        <div className="max-h-64 overflow-y-auto">
          {shown.map((opt) => {
            const on = selected.includes(opt);
            return (
              <Row key={opt} active={false} onClick={() => toggle(opt)}>
                <span
                  className={cn(
                    'grid size-[18px] place-items-center rounded-[5px] border',
                    on ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                  )}
                >
                  {on && <Check className="size-3.5" strokeWidth={3} />}
                </span>
                <span className="flex-1">{opt}</span>
              </Row>
            );
          })}
          {shown.length === 0 && (
            <div className="px-2 py-3 text-center text-sm text-muted-foreground">
              Ничего не найдено
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

type RowProps = {active: boolean; onClick: () => void; children: ReactNode};

const Row = ({active, onClick, children}: RowProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent',
      active && 'text-accent-foreground',
    )}
  >
    {children}
  </button>
);
